import { useEffect, useState } from "react";
import type {
  AdvertisementDto,
  AdvertisementStatus,
  AdvertisingBrandOptionDto,
  AdvertisingChannel,
  CreateAdvertisementPayload,
  UpdateAdvertisementPayload,
} from "@/api/advertising";
import { extractErrorMessage } from "@/api/client";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Button, Input, Label, Textarea } from "@/design-system/components/ui";
import {
  useCreateAdvertisement,
  useUpdateAdvertisement,
} from "@/hooks/buyers/useAdvertising";
import { US_STATES } from "@/lib/usStates";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import { CHANNEL_OPTIONS, STATUS_OPTIONS, formatPriceRange } from "./advertisingBits";

/** A money field's text → a number, or null when blank. */
function parseMoney(raw: string): number | null {
  return raw.trim() === "" ? null : Number(raw);
}

interface Props {
  open: boolean;
  onClose: () => void;
  buyerTeamId: string;
  /** Null creates; a row edits it in place. */
  advertisement: AdvertisementDto | null;
  /** Attribution layer 1's options, from the scope payload. */
  brands: AdvertisingBrandOptionDto[];
}

/**
 * Create or edit one advertisement (WS6).
 *
 * <p>The owning team is deliberately not editable. Moving an advertisement
 * between teams would move its spend history AND every lead it has already been
 * credited with, silently rewriting two months of both teams' cost-per-lead.</p>
 */
export function AdvertisementEditorModal({
  open, onClose, buyerTeamId, advertisement, brands,
}: Props) {
  const tz = useUserTimeZone();
  const isEdit = advertisement !== null;

  const [name, setName] = useState("");
  const [channel, setChannel] = useState<AdvertisingChannel>("BizBuySell");
  const [status, setStatus] = useState<AdvertisementStatus>("Draft");
  const [partnerBrandId, setPartnerBrandId] = useState("");
  const [plannedBudget, setPlannedBudget] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");
  const [adCopy, setAdCopy] = useState("");
  const [industry, setIndustry] = useState("");
  const [location, setLocation] = useState("");
  const [locationState, setLocationState] = useState("");
  const [targetPriceMin, setTargetPriceMin] = useState("");
  const [targetPriceMax, setTargetPriceMax] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Re-seeded on every open so a second edit never shows the first one's values.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setName(advertisement?.name ?? "");
    setChannel(advertisement?.channel ?? "BizBuySell");
    setStatus(advertisement?.status ?? "Draft");
    setPartnerBrandId(advertisement?.partnerBrandId ?? "");
    setPlannedBudget(
      advertisement?.metrics.plannedBudget === null
        || advertisement?.metrics.plannedBudget === undefined
        ? ""
        : String(advertisement.metrics.plannedBudget),
    );
    setStartDate(advertisement?.startDate ?? isoDateInTz(new Date(), tz));
    setEndDate(advertisement?.endDate ?? "");
    setNotes(advertisement?.notes ?? "");
    setAdCopy(advertisement?.adCopy ?? "");
    setIndustry(advertisement?.industry ?? "");
    setLocation(advertisement?.location ?? "");
    setLocationState(advertisement?.locationState ?? "");
    setTargetPriceMin(
      advertisement?.targetPriceMin === null || advertisement?.targetPriceMin === undefined
        ? "" : String(advertisement.targetPriceMin),
    );
    setTargetPriceMax(
      advertisement?.targetPriceMax === null || advertisement?.targetPriceMax === undefined
        ? "" : String(advertisement.targetPriceMax),
    );
  }, [open, advertisement, tz]);

  const create = useCreateAdvertisement();
  const update = useUpdateAdvertisement();
  const pending = create.isPending || update.isPending;

  const trimmedName = name.trim();
  const parsedBudget = plannedBudget.trim() === "" ? null : Number(plannedBudget);
  const budgetInvalid =
    parsedBudget !== null && (!Number.isFinite(parsedBudget) || parsedBudget < 0);
  const datesInvalid = endDate !== "" && startDate !== "" && endDate < startDate;

  const parsedMin = parseMoney(targetPriceMin);
  const parsedMax = parseMoney(targetPriceMax);
  const priceBoundInvalid = (v: number | null) =>
    v !== null && (!Number.isFinite(v) || v < 0);
  const priceRangeInvalid =
    priceBoundInvalid(parsedMin) || priceBoundInvalid(parsedMax)
    || (parsedMin !== null && parsedMax !== null && parsedMin > parsedMax);
  const priceRangeCleared = parsedMin === null && parsedMax === null;

  const canSave =
    trimmedName.length > 0 && !budgetInvalid && !datesInvalid && !priceRangeInvalid && !pending;

  async function submit() {
    setError(null);
    try {
      if (isEdit && advertisement) {
        // FIND-12 throughout. Every field is sent on every save because the
        // form hydrates all of them — an omitted field would mean "unchanged",
        // which is indistinguishable from a field the user cleared.
        const payload: UpdateAdvertisementPayload = {
          name: trimmedName,
          channel,
          status,
          startDate,
          notes: notes.trim(),
          // A nullable value type has no blank form, so clearing needs a flag.
          partnerBrandId: partnerBrandId || null,
          clearPartnerBrand: partnerBrandId === "",
          plannedBudget: parsedBudget,
          clearPlannedBudget: parsedBudget === null,
          endDate: endDate === "" ? null : endDate,
          clearEndDate: endDate === "",
          // "" clears the nullable strings (FIND-12) — sent trimmed, always.
          adCopy: adCopy.trim(),
          industry: industry.trim(),
          location: location.trim(),
          locationState,
          // The range clears as ONE flag when both bounds are blank. A single
          // blank bound rides as null, which FIND-12 reads as "unchanged" —
          // removing one bound while keeping the other is clear-then-set.
          targetPriceMin: parsedMin,
          targetPriceMax: parsedMax,
          clearTargetPriceRange: priceRangeCleared,
        };
        await update.mutateAsync({ id: advertisement.id, payload });
      } else {
        const payload: CreateAdvertisementPayload = {
          buyerTeamId,
          name: trimmedName,
          channel,
          status,
          partnerBrandId: partnerBrandId || null,
          plannedBudget: parsedBudget,
          startDate: startDate || null,
          endDate: endDate || null,
          notes: notes.trim() || null,
          adCopy: adCopy.trim() || null,
          industry: industry.trim() || null,
          location: location.trim() || null,
          locationState: locationState || null,
          targetPriceMin: parsedMin,
          targetPriceMax: parsedMax,
        };
        await create.mutateAsync(payload);
      }
      onClose();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit advertisement" : "New advertisement"}
      size="lg"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button onClick={submit} disabled={!canSave}>
            {pending ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="space-y-space-4">
        {error && (
          <p role="alert" className="text-caption text-status-error">{error}</p>
        )}

        <div>
          <Label htmlFor="ad-name">Name</Label>
          <Input
            id="ad-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Summer buyer acquisition"
          />
        </div>

        <div className="grid gap-space-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="ad-channel">Site / channel</Label>
            <Select
              id="ad-channel" value={channel}
              onChange={(e) => setChannel(e.target.value as AdvertisingChannel)}
            >
              {CHANNEL_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="ad-status">Status</Label>
            <Select
              id="ad-status" value={status}
              onChange={(e) => setStatus(e.target.value as AdvertisementStatus)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
            <p className="mt-space-1 text-caption text-gray-mid">
              Running and Paused advertisements claim inbound leads. A paused
              advertisement still earns credit for leads that arrive after it
              stops spending. Draft and Archived claim nothing.
            </p>
          </div>
        </div>

        <div>
          <Label htmlFor="ad-brand">Prequalification site</Label>
          <Select
            id="ad-brand" value={partnerBrandId}
            onChange={(e) => setPartnerBrandId(e.target.value)}
          >
            <option value="">None</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{b.displayName}</option>
            ))}
          </Select>
          <p className="mt-space-1 text-caption text-gray-mid">
            The attribution signal: arriving through a site is the evidence,
            and it is not something a visitor can mistype. Two live
            advertisements on one site attribute nothing — archive the retired
            one so its successor can claim the site.
          </p>
        </div>

        <div className="grid gap-space-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="ad-planned">Planned budget</Label>
            <Input
              id="ad-planned" type="text" inputMode="decimal" value={plannedBudget}
              onChange={(e) => setPlannedBudget(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div>
            <Label htmlFor="ad-start">Start date</Label>
            <Input
              id="ad-start" type="date" value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="ad-end">End date</Label>
            <Input
              id="ad-end" type="date" value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        {partnerBrandId === "" && (
          <p className="text-caption text-status-warning-ink">
            With no site, no lead can ever be attributed to this
            advertisement. Its spend will be tracked and its
            lead count will stay at zero.
          </p>
        )}

        {budgetInvalid && (
          <p role="alert" className="text-caption text-status-error">
            A planned budget cannot be negative.
          </p>
        )}
        {datesInvalid && (
          <p role="alert" className="text-caption text-status-error">
            The end date cannot be before the start date.
          </p>
        )}

        <div>
          <Label htmlFor="ad-copy">Ad copy</Label>
          <Textarea
            id="ad-copy" value={adCopy} onChange={(e) => setAdCopy(e.target.value)}
            rows={4}
            placeholder="The text of the placement, as the buyer read it."
          />
        </div>

        <div className="grid gap-space-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="ad-industry">Industry</Label>
            <Input
              id="ad-industry" value={industry} onChange={(e) => setIndustry(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div>
            <Label htmlFor="ad-location">Location</Label>
            <Input
              id="ad-location" value={location} onChange={(e) => setLocation(e.target.value)}
              placeholder="City or region"
            />
          </div>
          <div>
            <Label htmlFor="ad-state">State</Label>
            <Select
              id="ad-state" value={locationState}
              onChange={(e) => setLocationState(e.target.value)}
            >
              <option value="">Any</option>
              {US_STATES.map((st) => (
                <option key={st.code} value={st.code}>{st.name}</option>
              ))}
            </Select>
          </div>
        </div>

        <fieldset className="grid gap-space-4 sm:grid-cols-2">
          <legend className="mb-space-1 text-caption uppercase tracking-wide text-gray-mid">
            Target price range
          </legend>
          <div>
            <Label htmlFor="ad-price-min">Minimum</Label>
            <Input
              id="ad-price-min" type="text" inputMode="decimal" value={targetPriceMin}
              onChange={(e) => setTargetPriceMin(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div>
            <Label htmlFor="ad-price-max">Maximum</Label>
            <Input
              id="ad-price-max" type="text" inputMode="decimal" value={targetPriceMax}
              onChange={(e) => setTargetPriceMax(e.target.value)}
              placeholder="Optional"
            />
          </div>
          {!priceRangeInvalid && !priceRangeCleared && (
            <p className="text-caption text-gray-mid sm:col-span-2">
              Reads as {formatPriceRange(parsedMin, parsedMax)}.
            </p>
          )}
        </fieldset>

        {priceRangeInvalid && (
          <p role="alert" className="text-caption text-status-error">
            Target prices must be non-negative, and the minimum cannot exceed the maximum.
          </p>
        )}

        <div>
          <Label htmlFor="ad-notes">Notes</Label>
          <Input
            id="ad-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
